import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Container } from "../components/layout/Container";
import { cPath } from "../constants";
import { getAllStores } from "../services/storeServices";
import { createCouponSubmission } from "../services/submissionServices";
import useSEO from "../hooks/useSEO";

const Hero = styled.section`
  background: hsl(${({ theme }) => theme.colors.navDark});
  padding: 2.5rem 0;
  text-align: center;
  color: hsl(${({ theme }) => theme.colors.navDarkForeground});
`;

const H1 = styled.h1`
  margin: 0 0 0.5rem;
  font-size: ${({ theme }) => theme.font.heroTitle};
  font-weight: 800;
`;

const Body = styled(Container)`
  padding: 2rem 1rem 3rem;
  max-width: ${({ theme }) => theme.layout.formMax};
`;

const Label = styled.label`
  display: block;
  font-size: clamp(0.9375rem, 0.88rem + 0.2vw, 1.0625rem);
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const Input = styled.input`
  width: 100%;
  margin-bottom: 1rem;
  padding: 0.5rem 0.75rem;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  font-size: clamp(0.9375rem, 0.88rem + 0.2vw, 1.0625rem);
`;

const Select = styled.select`
  width: 100%;
  margin-bottom: 1rem;
  padding: 0.5rem 0.75rem;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  font-size: clamp(0.9375rem, 0.88rem + 0.2vw, 1.0625rem);
`;

const Txt = styled.textarea`
  width: 100%;
  min-height: 120px;
  margin-bottom: 1rem;
  padding: 0.5rem 0.75rem;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  font-family: inherit;
  font-size: clamp(0.9375rem, 0.88rem + 0.2vw, 1.0625rem);
`;

const Btn = styled.button`
  background: hsl(${({ theme }) => theme.colors.primary});
  color: hsl(${({ theme }) => theme.colors.primaryForeground});
  padding: 0.625rem 1.5rem;
  border-radius: ${({ theme }) => theme.radius.lg};
  font-weight: 600;
  font-size: clamp(0.9375rem, 0.88rem + 0.2vw, 1.0625rem);
  border: none;
  cursor: pointer;
  &:hover {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Err = styled.p`
  color: hsl(${({ theme }) => theme.colors.couponMaroon});
  font-size: 0.875rem;
`;

const Ok = styled.p`
  color: hsl(${({ theme }) => theme.colors.foreground});
  font-size: 0.9375rem;
`;

const HeroLead = styled.p`
  margin: 0;
  opacity: 0.75;
  font-size: clamp(0.9375rem, 0.88rem + 0.25vw, 1.125rem);
`;

export default function SubmitDealPage() {
  const [stores, setStores] = useState([]);
  const [storePublicId, setStorePublicId] = useState("");
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [code, setCode] = useState("");
  const [type, setType] = useState("code");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const list = await getAllStores();
        if (!cancel) setStores(list);
      } catch {
        if (!cancel) setStores([]);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!storePublicId || !title.trim() || !details.trim()) {
      setError("Choose a store and fill in title and details.");
      return;
    }
    setLoading(true);
    try {
      await createCouponSubmission({
        storePublicId,
        title: title.trim(),
        description: details.trim(),
        code: code.trim() || undefined,
        type,
      });
      setDone(true);
      setTitle("");
      setDetails("");
      setCode("");
      setStorePublicId("");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not submit. Store must be published in our catalog."
      );
    } finally {
      setLoading(false);
    }
  };

  const seo = useSEO({ noindex: true, nofollow: true });

  return (
    <>
      {seo}
      <Hero>
        <Container>
          <H1>Submit a Deal</H1>
          <HeroLead>Propose a coupon for an existing store. Admins review before it goes live.</HeroLead>
        </Container>
      </Hero>
      <Body>
        {done ? (
          <Ok>
            Thanks — your proposal was received.{" "}
            <button
              type="button"
              style={{
                background: "none",
                border: "none",
                color: "inherit",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => setDone(false)}
            >
              Submit another
            </button>{" "}
            or <Link to={cPath("")}>return home</Link>.
          </Ok>
        ) : (
          <form onSubmit={submit}>
            {error ? <Err>{error}</Err> : null}
            <Label htmlFor="sd-store">Store *</Label>
            <Select
              id="sd-store"
              value={storePublicId}
              onChange={(e) => setStorePublicId(e.target.value)}
              required
            >
              <option value="">Select store…</option>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
            <Label htmlFor="sd-title">Deal title *</Label>
            <Input
              id="sd-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder="Short headline"
            />
            <Label htmlFor="sd-type">Type</Label>
            <Select id="sd-type" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="code">Code</option>
              <option value="deal">Deal</option>
              <option value="sale">Sale</option>
            </Select>
            {type === "code" ? (
              <>
                <Label htmlFor="sd-code">Coupon code</Label>
                <Input
                  id="sd-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Optional"
                />
              </>
            ) : null}
            <Label htmlFor="sd-det">Details *</Label>
            <Txt
              id="sd-det"
              required
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Describe the offer, restrictions, and expiry."
            />
            <Btn type="submit" disabled={loading}>
              {loading ? "Submitting…" : "Submit for review"}
            </Btn>
          </form>
        )}
      </Body>
    </>
  );
}
